import { eq } from 'drizzle-orm'
import { useEffect, useRef, useState } from 'react'
import {
	type ActionFunctionArgs,
	Form,
	type LoaderFunctionArgs,
	data,
	useActionData,
	useLoaderData,
} from 'react-router'
import { FloatingLabelField, FloatingSelectField } from '#app/components/forms'
import { Button } from '#app/components/ui/button'
import { db } from '#app/utils/db.server'
import { user } from '#db/schema/base'
import { properties } from '#db/schema/properties'
import { tenantScreeningApplication } from '#db/schema/tenantScreeningApplication'
import { userTencheckRecord } from '#db/schema/userTencheckRecord'

// Types for Tencheck API
interface TenCheckConfig {
	apiUrl: string
	user: string
	pass: string
}

interface TenCheckAuthResponse {
	token?: string
	[key: string]: unknown
}

interface TencheckConsumerRequest {
	property: {
		buildingCode: string
	}
	firstName: string
	middleName?: string
	lastName: string
	dob: string // YYYY-MM-DD
	address: {
		street: string
		city: string
		province: string
		postalCode: string
		country: string
	}
	moveIn: string // YYYY-MM-DD
	json: number
}

interface TencheckConsumerResponse {
	[key: string]: unknown
}

type ActionData =
	| {
			success: true
			message: string
			recordId: string
	  }
	| { error: string }
	| null

// Helper function to get TenCheck configuration
function getTenCheckConfig(): TenCheckConfig {
	const apiUrl = process.env.TENCHEK_API_URL
	const tencheckUser = process.env.TENCHEK_USER
	const tencheckPass = process.env.TENCHEK_PASS

	if (!apiUrl || !tencheckUser || !tencheckPass) {
		throw new Error(
			'TenCheck is not configured. Set TENCHEK_API_URL, TENCHEK_USER and TENCHEK_PASS.',
		)
	}

	return {
		apiUrl: apiUrl.replace(/\/$/, ''), // Remove trailing slash
		user: tencheckUser,
		pass: tencheckPass,
	}
}

/**
 * Authenticate with TenCheck API
 * Calls POST https://www.tenchek.com/api/Auth/Login
 * @returns Bearer token
 */
async function authenticateTencheck(): Promise<string> {
	const config = getTenCheckConfig()
	const url = `${config.apiUrl}/api/Auth/Login`

	console.log('[Tencheck External] Authenticating...', { url })

	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				user: config.user,
				pass: config.pass,
			}),
		})

		if (!response.ok) {
			const errorText = await response.text()
			console.error('[Tencheck External] Authentication failed:', {
				status: response.status,
				statusText: response.statusText,
				error: errorText,
			})
			throw new Error(
				`Tencheck authentication failed: ${response.status} ${response.statusText} - ${errorText}`,
			)
		}

		const data = (await response.json()) as TenCheckAuthResponse

		// Extract token - could be in different fields depending on API response
		const token =
			data.token ||
			(data as { bearer?: string }).bearer ||
			(data as { accessToken?: string }).accessToken

		if (!token) {
			console.error('[Tencheck External] Auth response missing token:', data)
			throw new Error('Tencheck authentication response missing token')
		}

		console.log('[Tencheck External] Authentication successful')
		return typeof token === 'string' ? token : String(token)
	} catch (error) {
		console.error('[Tencheck External] Error authenticating:', error)
		if (error instanceof Error) {
			throw error
		}
		throw new Error('Failed to authenticate with Tencheck')
	}
}

/**
 * Request Tencheck Consumer report
 * @param token - Authentication token
 * @param payload - Consumer request payload
 * @returns Full response from Tencheck API
 */
async function requestTencheckConsumer(
	token: string,
	payload: TencheckConsumerRequest,
): Promise<TencheckConsumerResponse> {
	const config = getTenCheckConfig()
	const url = `${config.apiUrl}/api/Request/Consumer`

	console.log('[TenCheck External] Requesting consumer report...', { url })

	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				accept: 'application/json',
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify(payload),
		})

		if (!response.ok) {
			const errorText = await response.text()
			console.error('[TenCheck External] Request consumer failed:', {
				status: response.status,
				statusText: response.statusText,
				error: errorText,
			})
			throw new Error(
				`TenCheck request consumer failed: ${response.status} ${response.statusText} - ${errorText}`,
			)
		}

		const data = (await response.json()) as TencheckConsumerResponse
		console.log('[TenCheck External] Consumer report requested successfully')

		return data
	} catch (error) {
		console.error(
			'[TenCheck External] Error requesting consumer report:',
			error,
		)
		if (error instanceof Error) {
			throw error
		}
		throw new Error('Failed to request TenCheck consumer report')
	}
}

// Helper function to get tenantId from hostname
async function getTenantIdFromHostname(hostname: string): Promise<string> {
	const { tenant } = await import('#db/schema/base')
	const existingTenant = await db.query.tenant.findFirst({
		where: (t, { eq }) => eq(t.hostname, hostname),
	})

	if (existingTenant) {
		return existingTenant.id
	}

	// Fallback to TrueTenant or default tenant
	const trueTenant = await db.query.tenant.findFirst({
		where: (t, { eq, or: orFn }) =>
			orFn(
				eq(t.name, 'TrueTenant'),
				eq(t.hostname, 'localhost'),
				eq(t.hostname, 'truetenant.ai'),
			),
	})

	if (!trueTenant) {
		throw new Error(
			'No tenant found for this hostname. Please ensure tenant is configured.',
		)
	}

	return trueTenant.id
}

export async function loader({ request }: LoaderFunctionArgs) {
	try {
		const hostname = new URL(request.url).hostname
		const tenantId = await getTenantIdFromHostname(hostname)

		// Fetch properties for dropdown
		const dbProperties = await db.query.properties.findMany({
			where: eq(properties.tenantId, tenantId),
			orderBy: (props, { desc }) => [desc(props.createdAt)],
		})

		const propertyOptions = dbProperties.map((p) => ({
			label: p.propertyName || p.address || 'Unknown Property',
			value: p.id,
		}))

		// Fetch users for dropdown
		const dbUsers = await db.query.user.findMany({
			where: eq(user.tenantId, tenantId),
			columns: {
				id: true,
				name: true,
				email: true,
				username: true,
			},
		})

		const userOptions = dbUsers.map((u) => ({
			label: u.name || u.email || u.username || 'Unknown User',
			value: u.id,
		}))

		return {
			properties: propertyOptions,
			users: userOptions,
		}
	} catch (error) {
		console.error('[Tencheck External] Loader error:', error)
		return {
			properties: [],
			users: [],
			error: error instanceof Error ? error.message : 'Failed to load data',
		}
	}
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()

	try {
		// Extract and validate form data
		const propertyId = formData.get('property')?.toString()
		const userId = formData.get('user')?.toString()
		const firstName = formData.get('first_name')?.toString()
		const lastName = formData.get('last_name')?.toString()
		const dateOfBirth = formData.get('date_of_birth')?.toString()
		const street = formData.get('street')?.toString()
		const city = formData.get('city')?.toString()
		const province = formData.get('province')?.toString()
		const postalCode = formData.get('postal_code')?.toString()
		const country = formData.get('country')?.toString()
		const moveInDate = formData.get('move_in_date')?.toString()

		// Validate required fields
		if (!propertyId) {
			return data({ error: 'Property is required' }, { status: 400 })
		}
		if (!userId) {
			return data({ error: 'User is required' }, { status: 400 })
		}
		if (!firstName) {
			return data({ error: 'First name is required' }, { status: 400 })
		}
		if (!lastName) {
			return data({ error: 'Last name is required' }, { status: 400 })
		}
		if (!dateOfBirth) {
			return data({ error: 'Date of birth is required' }, { status: 400 })
		}
		if (!street) {
			return data({ error: 'Street address is required' }, { status: 400 })
		}
		if (!city) {
			return data({ error: 'City is required' }, { status: 400 })
		}
		if (!province) {
			return data({ error: 'Province is required' }, { status: 400 })
		}
		if (!postalCode) {
			return data({ error: 'Postal code is required' }, { status: 400 })
		}
		if (!country) {
			return data({ error: 'Country is required' }, { status: 400 })
		}
		if (!moveInDate) {
			return data({ error: 'Move-in date is required' }, { status: 400 })
		}

		// Validate date formats
		const dobRegex = /^\d{4}-\d{2}-\d{2}$/
		if (!dobRegex.test(dateOfBirth)) {
			return data(
				{ error: 'Date of birth must be in YYYY-MM-DD format' },
				{ status: 400 },
			)
		}
		if (!dobRegex.test(moveInDate)) {
			return data(
				{ error: 'Move-in date must be in YYYY-MM-DD format' },
				{ status: 400 },
			)
		}

		// Get tenantId from hostname
		const hostname = new URL(request.url).hostname
		const tenantId = await getTenantIdFromHostname(hostname)

		// Fetch property to get building_code
		const property = await db.query.properties.findFirst({
			where: eq(properties.id, propertyId),
			columns: {
				id: true,
				buildingCode: true,
			},
		})

		if (!property) {
			return data({ error: 'Property not found' }, { status: 404 })
		}

		if (!property.buildingCode) {
			return data(
				{ error: 'Property does not have a building code' },
				{ status: 400 },
			)
		}

		// Verify user exists
		const dbUser = await db.query.user.findFirst({
			where: eq(user.id, userId),
			columns: { id: true },
		})

		if (!dbUser) {
			return data({ error: 'User not found' }, { status: 404 })
		}

		// Create a minimal screening application for this external request
		// (required by user_tencheck_records schema)
		const [screeningApplication] = await db
			.insert(tenantScreeningApplication)
			.values({
				userId,
				tenantId,
				propertyId,
				status: 'in_progress',
			})
			.returning({ id: tenantScreeningApplication.id })

		// Build Tencheck API payload
		const payload: TencheckConsumerRequest = {
			property: {
				buildingCode: property.buildingCode,
			},
			firstName: firstName.toUpperCase(),
			lastName: lastName.toUpperCase(),
			dob: dateOfBirth,
			address: {
				street,
				city,
				province: province.toUpperCase(),
				postalCode: postalCode.replace(/\s/g, '').toUpperCase(),
				country: country.toUpperCase(),
			},
			moveIn: moveInDate,
			json: 1,
		}

		// Authenticate with Tencheck
		const token = await authenticateTencheck()

		// Request consumer report
		const tencheckResponse: any = await requestTencheckConsumer(token, payload)
		const tencheckJsonRes = tencheckResponse?.json
		const parseTencheckJsonRes: any = JSON.parse(tencheckResponse.json)

		const checkId = parseTencheckJsonRes?.id

		// Extract checkId from response
		// const checkId =
		// 	(tencheckResponse.checkId as string) ||
		// 	(tencheckResponse.id as string) ||
		// 	null

		// Store both request and response payloads in tencheckResponse
		const combinedResponse: Record<string, unknown> = {
			requestPayload: payload,
			responsePayload: tencheckResponse,
			propertyId,
		}

		// Store response in user_tencheck_records
		const [tencheckRecord] = await db
			.insert(userTencheckRecord)
			.values({
				userId,
				responseData: parseTencheckJsonRes,
				screeningApplicationId: screeningApplication.id,
				status: 'completed',
				tencheckResponse: combinedResponse,
				checkId,
				errorMessage: null,
			})
			.returning({ id: userTencheckRecord.id })

		return data({
			success: true,
			message: 'Tencheck API call completed successfully',
			recordId: tencheckRecord.id,
		})
	} catch (error) {
		console.error('[Tencheck External] Action error:', error)
		const errorMessage =
			error instanceof Error
				? error.message
				: 'Failed to process Tencheck request'

		return data({ error: errorMessage }, { status: 500 })
	}
}

export default function TencheckExternalRoute() {
	const { properties: propertyOptions, users: userOptions } =
		useLoaderData<typeof loader>()
	const actionData = useActionData<ActionData>()
	const [isSubmitting, setIsSubmitting] = useState(false)
	const formRef = useRef<HTMLFormElement>(null)
	const [formData, setFormData] = useState({
		property: '',
		user: '',
	})

	// Reset form on successful submission
	useEffect(() => {
		if (
			actionData &&
			'success' in actionData &&
			actionData.success &&
			formRef.current
		) {
			setIsSubmitting(false)
			setFormData({ property: '', user: '' })
			formRef.current.reset()
		} else if (actionData && 'error' in actionData) {
			setIsSubmitting(false)
		}
	}, [actionData])

	return (
		<div className="container mx-auto max-w-2xl py-8 px-4">
			<div className="space-y-6">
				<div>
					<h1 className="text-3xl font-bold">Tencheck API Form</h1>
					<p className="text-muted-foreground mt-2">
						Submit a Tencheck credit check request
					</p>
				</div>

				{actionData && 'error' in actionData && (
					<div className="rounded-lg bg-destructive/10 border border-destructive p-4">
						<p className="text-sm text-destructive font-medium">Error</p>
						<p className="text-sm text-destructive/80 mt-1">
							{actionData.error}
						</p>
					</div>
				)}

				{actionData && 'success' in actionData && actionData.success && (
					<div className="rounded-lg bg-green-50 border border-green-200 p-4">
						<p className="text-sm text-green-800 font-medium">Success</p>
						<p className="text-sm text-green-700 mt-1">{actionData.message}</p>
						<p className="text-xs text-green-600 mt-1">
							Record ID: {actionData.recordId}
						</p>
					</div>
				)}

				<Form
					ref={formRef}
					method="post"
					className="space-y-4"
					onSubmit={() => setIsSubmitting(true)}
				>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<FloatingSelectField
								labelProps={{ children: 'Property' }}
								value={formData.property}
								onChange={(value) =>
									setFormData((prev) => ({ ...prev, property: value }))
								}
								options={propertyOptions}
							/>
							<input type="hidden" name="property" value={formData.property} />
						</div>

						<div>
							<FloatingSelectField
								labelProps={{ children: 'User' }}
								value={formData.user}
								onChange={(value) =>
									setFormData((prev) => ({ ...prev, user: value }))
								}
								options={userOptions}
							/>
							<input type="hidden" name="user" value={formData.user} />
						</div>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<FloatingLabelField
							labelProps={{ children: 'First Name' }}
							inputProps={{
								name: 'first_name',
								type: 'text',
								required: true,
							}}
						/>

						<FloatingLabelField
							labelProps={{ children: 'Last Name' }}
							inputProps={{
								name: 'last_name',
								type: 'text',
								required: true,
							}}
						/>
					</div>

					<FloatingLabelField
						labelProps={{ children: 'Date of Birth (YYYY-MM-DD)' }}
						inputProps={{
							name: 'date_of_birth',
							type: 'date',
							required: true,
						}}
					/>

					<FloatingLabelField
						labelProps={{ children: 'Street Address' }}
						inputProps={{
							name: 'street',
							type: 'text',
							required: true,
						}}
					/>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<FloatingLabelField
							labelProps={{ children: 'City' }}
							inputProps={{
								name: 'city',
								type: 'text',
								required: true,
							}}
						/>

						<FloatingLabelField
							labelProps={{ children: 'Province' }}
							inputProps={{
								name: 'province',
								type: 'text',
								required: true,
							}}
						/>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<FloatingLabelField
							labelProps={{ children: 'Postal Code' }}
							inputProps={{
								name: 'postal_code',
								type: 'text',
								required: true,
							}}
						/>

						<FloatingLabelField
							labelProps={{ children: 'Country' }}
							inputProps={{
								name: 'country',
								type: 'text',
								required: true,
								defaultValue: 'CA',
							}}
						/>
					</div>

					<FloatingLabelField
						labelProps={{ children: 'Move-in Date (YYYY-MM-DD)' }}
						inputProps={{
							name: 'move_in_date',
							type: 'date',
							required: true,
						}}
					/>

					<Button type="submit" disabled={isSubmitting} className="w-full">
						{isSubmitting ? 'Submitting...' : 'Submit Tencheck Request'}
					</Button>
				</Form>
			</div>
		</div>
	)
}
